import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faXTwitter,
	faLinkedinIn,
	faReddit,
	faHackerNews,
} from "@fortawesome/free-brands-svg-icons";
import { faLink, faCheck, faShareNodes } from "@fortawesome/free-solid-svg-icons";

import { track } from "../../lib/analytics";

import "./styles/shareBar.css";

// Reusable share row for articles and tools. The goal is that when a piece
// gets discovered (by press, an influencer, a link that spreads), the reader
// can pass it on in one click to the places our audience actually lives:
// X, LinkedIn, Reddit, Hacker News, plus copy-link and the native mobile
// share sheet. The URL is read at click time so the component always shares
// whatever route the visitor is currently on.
const ShareBar = ({ title, kind = "page", prompt = "Found this useful? Share it" }) => {
	const [copied, setCopied] = useState(false);
	const [canNativeShare] = useState(
		() => typeof navigator !== "undefined" && !!navigator.share
	);

	const getUrl = () =>
		typeof window !== "undefined" ? window.location.href : "";

	const openShare = (network, href) => {
		track("share", { network, kind, page: getUrl() });
		window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
	};

	const shareX = () => {
		const url = getUrl();
		openShare(
			"x",
			`https://twitter.com/intent/tweet?text=${encodeURIComponent(
				title
			)}&url=${encodeURIComponent(url)}`
		);
	};

	const shareLinkedIn = () => {
		const url = getUrl();
		openShare(
			"linkedin",
			`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
				url
			)}`
		);
	};

	const shareReddit = () => {
		const url = getUrl();
		openShare(
			"reddit",
			`https://www.reddit.com/submit?url=${encodeURIComponent(
				url
			)}&title=${encodeURIComponent(title)}`
		);
	};

	const shareHackerNews = () => {
		const url = getUrl();
		openShare(
			"hackernews",
			`https://news.ycombinator.com/submitlink?u=${encodeURIComponent(
				url
			)}&t=${encodeURIComponent(title)}`
		);
	};

	const nativeShare = async () => {
		const url = getUrl();
		track("share", { network: "native", kind, page: url });
		try {
			await navigator.share({ title, text: title, url });
		} catch (e) {
			/* user dismissed the share sheet; nothing to do */
		}
	};

	const copyLink = async () => {
		const url = getUrl();
		track("share", { network: "copy", kind, page: url });
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (e) {
			/* clipboard blocked; the visible URL is still selectable */
		}
	};

	return (
		<div className="share-bar">
			{prompt ? <span className="share-bar-prompt">{prompt}</span> : null}
			<div className="share-bar-buttons">
				<button
					type="button"
					className="share-btn share-btn-x"
					onClick={shareX}
					aria-label="Share on X"
					title="Share on X"
				>
					<FontAwesomeIcon icon={faXTwitter} />
				</button>
				<button
					type="button"
					className="share-btn share-btn-linkedin"
					onClick={shareLinkedIn}
					aria-label="Share on LinkedIn"
					title="Share on LinkedIn"
				>
					<FontAwesomeIcon icon={faLinkedinIn} />
				</button>
				<button
					type="button"
					className="share-btn share-btn-reddit"
					onClick={shareReddit}
					aria-label="Share on Reddit"
					title="Share on Reddit"
				>
					<FontAwesomeIcon icon={faReddit} />
				</button>
				<button
					type="button"
					className="share-btn share-btn-hn"
					onClick={shareHackerNews}
					aria-label="Share on Hacker News"
					title="Share on Hacker News"
				>
					<FontAwesomeIcon icon={faHackerNews} />
				</button>
				{canNativeShare ? (
					<button
						type="button"
						className="share-btn share-btn-native"
						onClick={nativeShare}
						aria-label="Share"
						title="Share"
					>
						<FontAwesomeIcon icon={faShareNodes} />
					</button>
				) : null}
				<button
					type="button"
					className={`share-btn share-btn-copy${
						copied ? " is-copied" : ""
					}`}
					onClick={copyLink}
					aria-label={copied ? "Link copied" : "Copy link"}
					title={copied ? "Link copied" : "Copy link"}
				>
					<FontAwesomeIcon icon={copied ? faCheck : faLink} />
					<span className="share-btn-copy-text">
						{copied ? "Copied" : "Copy link"}
					</span>
				</button>
			</div>
		</div>
	);
};

export default ShareBar;
